import {
	Box,
	Button,
	Card,
	Divider,
	Grid,
	Tab,
	Tabs,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FormType } from '@/api/enum/formType.enum';
import { useState } from 'react';
import AppFieldView from '@/common/components/form-view/AppFieldView';

import { useParams } from 'react-router-dom';
import { DISPLAY_DATETIME_12H_FORMAT } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import { useGetHealthDetailQuery } from '@/api/app-setting/healthApiSlice';
import HealthInfoForm from './components/HealthInfoForm';
import RichTextDisplay from '@/common/components/form-view/RichTextDisplay';
import HealthDetailListSummaryPage from './HealthDetailListSummaryPage';

export interface DataGrid {
	updatedAt: string;
	createdAt: string;
}
export default function HealthDetailPage() {
	const { t } = useTranslation();
	const [type, setType] = useState<FormType>(FormType.view);
	const { healthId } = useParams();
	const [currentTab, setCurrentTab] = useState('zh');

	const { data: details } = useGetHealthDetailQuery(healthId || '', {
		refetchOnMountOrArgChange: true,
	});

	const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
		setCurrentTab(newValue);
	};

	return (
		<>
			<Box sx={{ width: '100%' }}>
				<Box display='flex' justifyContent='space-between'>
					<Typography variant='h4'>
						{t('drawerListItems.healthDetail')}
					</Typography>
				</Box>

				<Card
					sx={{ padding: type == FormType.view ? '0 24px' : 0, marginTop: 3 }}
				>
					<Box sx={{ padding: type == FormType.view ? 0 : '0 24px' }}>
						<Box
							display='flex'
							justifyContent='space-between'
							sx={{ paddingTop: '24px' }}
						>
							<Typography variant='h6'>{t('health.record')}</Typography>
						</Box>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid item xs={6}>
								<AppFieldView
									label={t('health.resultType')}
									value={details?.data.typeLabel}
								/>
							</Grid>
							<Grid item xs={6}>
								<AppFieldView
									label={t('health.createTime')}
									value={
										!details?.data.createdAt
											? '-'
											: dayjs(details?.data.createdAt)
													.locale('en')
													.format(DISPLAY_DATETIME_12H_FORMAT)
									}
								/>
							</Grid>
							<Grid item xs={6}>
								<AppFieldView
									label={t('health.lastUpdateTime')}
									value={
										!details?.data.updatedAt
											? '-'
											: dayjs(details?.data.updatedAt)
													.locale('en')
													.format(DISPLAY_DATETIME_12H_FORMAT)
									}
								/>
							</Grid>
						</Grid>
					</Box>

					<Divider />
					{(() => {
						switch (type) {
							case FormType.view:
								return (
									<>
										<Box
											display='flex'
											justifyContent='space-between'
											sx={{ paddingTop: '24px' }}
										>
											<Typography variant='h6'>{t('health.text')}</Typography>
											<Button
												endIcon={<CreateOutlinedIcon />}
												color='secondary'
												onClick={() => {
													setType(FormType.edit);
												}}
											>
												{t('button.edit')}
											</Button>
										</Box>
										<Grid container spacing={3} marginTop={0} marginBottom={3}>
											<Grid item xs={12}>
												<Tabs value={currentTab} onChange={handleTabChange}>
													<Tab
														label={
															<Typography variant='body1'>
																{t('health.zh')}
															</Typography>
														}
														value='zh'
													/>
													<Tab
														label={
															<Typography variant='body1'>
																{t('health.en')}
															</Typography>
														}
														value='en'
													/>
												</Tabs>
											</Grid>
											<Grid item xs={6}>
												<RichTextDisplay
													label={t('health.resultContent')}
													editorStateString={
														currentTab == 'zh'
															? details?.data.contentZh
															: currentTab == 'en'
															? details?.data.contentEn
															: '-'
													}
												/>
											</Grid>
											<Grid item xs={6}>
												<RichTextDisplay
													label={t('health.healthTips')}
													editorStateString={
														currentTab == 'zh'
															? details?.data.tipsZh
															: currentTab == 'en'
															? details?.data.tipsEn
															: '-'
													}
												/>
											</Grid>
										</Grid>
									</>
								);
							case FormType.edit:
								return (
									<>
										<Box
											display='flex'
											justifyContent='space-between'
											sx={{ padding: '24px', paddingBottom: '0' }}
										>
											<Typography variant='h6'>{t('health.text')}</Typography>
										</Box>
										<HealthInfoForm
											type={type}
											setType={setType}
											details={details?.data}
										/>
									</>
								);
						}
					})()}
				</Card>
				<Card sx={{ padding: '24px', marginTop: 3 }}>
					<HealthDetailListSummaryPage
						exerciseList={details?.data.exerciseList || []}
					/>
				</Card>
			</Box>
		</>
	);
}
